import CIcon from '@coreui/icons-react';
import { CButton, CForm } from '@coreui/react';
import React, { Fragment, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { Formik } from 'formik';
import * as Yup from 'yup';
import get from 'lodash/get';
import {
     handleFetchCountFormsInventory,
     handleFetchFormsInventory,
     handleFetchCountFormsRequiring,
     handleFetchFormsRequiring,
     setAllFormsInventory,
     setAllFormsRequiring,
     setFilterEditFormCategories,
     setFilterFormCategories,
     setFormInventory,
     setFormInventoryEdit,
     setFormRequiringAction,
     setFormRequiringEdit,
} from '../../../../../../actions/subscriber';
import { CInput } from '../../../../../migration/CInput';
import { CInvalidFeedback } from '../../../../../migration/CInvalidFeedback';
import CLabel from '../../../../../migration/CLabel';
import CFormGroup from '../../../../../migration/CFormGroup';
import PropTypes from 'prop-types';

const validationSchema = () => {
     return Yup.array(
          Yup.object().shape({
               type: Yup.string().trim().required('This field is required!'),
               value: Yup.string()
                    .nullable()
                    .when('operator', {
                         is: (operator) => operator !== 'nex',
                         then: (schema) => schema.required('This field is required!'),
                         otherwise: (schema) => schema,
                    }),
          }),
     );
};
const FormFilter = ({ setShowTooltip, formType = 'inventory' }) => {
     const dispatch = useDispatch();
     const activeAccount = useSelector((state) => state.subscriber.activeAccount);
     const listCustomCategories = useSelector((state) => state.subscriber.customCategories);
     const filterFormCategories = useSelector((state) => state.subscriber.filterFormCategories);
     const filterEditFormCategories = useSelector((state) => state.subscriber.filterEditFormCategories);
     const accountListeners = useSelector((state) => state.subscriber.accountListeners);

     const categoryOptions = useMemo(() => {
          const newListCategories =
               listCustomCategories?.length > 0
                    ? [{ id: '', name: 'Select a category', value: null }, ...listCustomCategories]
                    : [];

          return newListCategories ? newListCategories.map((category) => ({ value: category.id, label: category.name })) : [];
     }, [listCustomCategories]);

     const listenerNameOptions = useMemo(() => {
          if (!accountListeners || accountListeners.length === 0) return [];
          
          // Filter only listeners with type 'Forms'
          const formListeners = accountListeners.filter((listener) => {
               const type = (listener.type || '').toLowerCase();
               return type === 'forms';
          });
          
          return formListeners.map((listener) => ({ value: listener.name, label: listener.name }));
     }, [accountListeners]);

     const initialValue = useMemo(() => [{ type: '', operator: 'ct', value: '' }], []);

     let filterOptions = [
          { label: 'Listener Name', value: 'listenerName' },
          { label: 'Form ID', value: 'formValue' },
          { label: 'Form Location', value: 'formLocation' },
          { label: 'Form Category', value: 'categoryId' },
          { label: 'Form Name', value: 'name' },
     ];

     if (formType === 'requiring') {
          filterOptions = filterOptions.filter((option) => option.value !== 'categoryId' && option.value !== 'name');
     }

     const operatorOptions = [
          { value: 'ct', label: 'contains' },
          { value: 'nli', label: 'not contain' },
          { value: 'eq', label: 'equals to' },
          { value: 'neq', label: 'not equal to' },
          { value: 'nex', label: 'does not exist' },
          { value: 'checklist', label: 'checklist' },
          { value: 'notChecklist', label: 'not contain checklist' },
     ];

     const handleSubmit = (values) => {
          const newValues = values.map((value) => {
               if (value.operator === 'nex') {
                    return { ...value, operator: 'eq' };
               }

               if (value.operator === 'checklist') {
                    // Convert array to comma-separated string for API
                    const valueStr = Array.isArray(value.value) ? value.value.join(',') : value.value;
                    return { ...value, operator: 'in', value: valueStr };
               }

               if (value.operator === 'notChecklist') {
                    // Convert array to comma-separated string for API
                    const valueStr = Array.isArray(value.value) ? value.value.join(',') : value.value;
                    return { ...value, operator: 'notIn', value: valueStr };
               }

               return value;
          });
          
          dispatch(setFilterFormCategories(newValues));
          
          if (formType === 'inventory') {
               // Handle inventory form filtering
               dispatch(setFormInventory(null));
               dispatch(setAllFormsInventory(null));
               dispatch(setFormInventoryEdit(null));
               dispatch(handleFetchCountFormsInventory(null, activeAccount, newValues));
               dispatch(handleFetchFormsInventory(20, false, 0, newValues));
          } else if (formType === 'requiring') {
               // Handle requiring form filtering
               dispatch(setFormRequiringAction(null));
               dispatch(setAllFormsRequiring(null));
               dispatch(setFormRequiringEdit(null));
               dispatch(handleFetchCountFormsRequiring(null, null, activeAccount, newValues));
               dispatch(handleFetchFormsRequiring(20, false, 0, newValues));
          }
     };

     const initFormValues = useMemo(() => {
          const newInitialValue = filterEditFormCategories || initialValue;
          if (formType === 'requiring') {
               return newInitialValue.filter((item) => item.type !== 'categoryId' && item.type !== 'name');
          }
          return newInitialValue;
     }, [filterEditFormCategories, initialValue, formType]);

     return (
          <div className="form-filter">
               <Formik
                    initialValues={initFormValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    validateOnChange={false}
                    validateOnBlur={false}
               >
                    {({ values, errors, handleSubmit, setErrors, setValues }) => {
                         const handleChangeFilter = ({ valueFilter, position, type, setValues, item }) => {
                              const { value } = valueFilter;
                              const newFilter = [...values];
                              if ((item.type === 'categoryId' && type === 'type') || (value === 'nex' && type === 'operator') || (value === 'checklist' && type === 'operator') || (value === 'notChecklist' && type === 'operator')) {
                                   newFilter[position] = { ...newFilter[position], [`${type}`]: value, value: '' };
                              } else {
                                   newFilter[position] = { ...newFilter[position], [`${type}`]: value };
                              }

                              if (value === 'categoryId' && (item.operator === 'ct' || item.operator === 'nli')) {
                                   newFilter[position] = { ...newFilter[position], type: 'categoryId', operator: 'eq', value: '' };
                              }

                              if (type === 'type' && value !== 'categoryId' && value !== 'name' && item.operator === 'nex') {
                                   newFilter[position] = { ...newFilter[position], operator: 'ct' };
                              }

                              setValues(newFilter);
                              dispatch(setFilterEditFormCategories(newFilter));
                              setErrors({});
                         };

                         const handleChangeValueFilter = (e, index, setValues) => {
                              const { name, value } = e.target;
                              const newFilter = [...values];
                              newFilter[index] = { ...newFilter[index], [name]: value };
                              setValues(newFilter);
                              dispatch(setFilterEditFormCategories(newFilter));
                         };

                         const handleChangeCategory = (e, index, setValues) => {
                              const { value } = e;
                              const newFilter = [...values];
                              newFilter[index] = { ...newFilter[index], value: value };
                              setValues(newFilter);
                              dispatch(setFilterEditFormCategories(newFilter));
                         };

                         const handleChangeChecklist = (selectedOptions, index, setValues) => {
                              // Handle multi-select - selectedOptions is an array
                              const newValue = selectedOptions ? selectedOptions.map((option) => option.value) : [];
                              const newFilter = [...values];
                              newFilter[index] = { ...newFilter[index], value: newValue };
                              setValues(newFilter);
                              dispatch(setFilterEditFormCategories(newFilter));
                         };

                         const handleDeleteFilter = (index, setValues) => {
                              setTimeout(() => {
                                   const newFilter = [...values];
                                   newFilter.splice(index, 1);
                                   setValues(newFilter);
                                   dispatch(setFilterEditFormCategories(newFilter));
                                   setErrors({});
                              }, 100);
                         };

                         const handleResetAll = () => {
                              setValues(initialValue);
                              setErrors({});
                              dispatch(setFilterFormCategories([]));
                              dispatch(setFilterEditFormCategories(initialValue));

                              if (filterFormCategories?.length > 0 && values.some((item) => item.type)) {
                                   if (formType === 'inventory') {
                                        dispatch(handleFetchCountFormsInventory(null, activeAccount));
                                        dispatch(handleFetchFormsInventory(20));
                                   } else if (formType === 'requiring') {
                                        dispatch(handleFetchCountFormsRequiring(null, null, activeAccount));
                                        dispatch(handleFetchFormsRequiring(20));
                                   }
                              }
                         };

                         const getOperatorOptions = (item) => {
                              let newOperatorOptions = [];

                              if (item.type === 'categoryId') {
                                   newOperatorOptions = operatorOptions.filter(
                                        (option) => option.value === 'eq' || option.value === 'neq' || option.value === 'nex',
                                   );
                              } else if (item.type === 'listenerName') {
                                   newOperatorOptions = operatorOptions.filter(
                                        (option) => option.value !== 'nex',
                                   );
                              } else if (item.type !== 'categoryId' && item.type !== 'name') {
                                   newOperatorOptions = operatorOptions.filter((option) => option.value !== 'nex' && option.value !== 'checklist' && option.value !== 'notChecklist');
                              } else {
                                   newOperatorOptions = operatorOptions.filter((option) => option.value !== 'checklist' && option.value !== 'notChecklist');
                              }

                              return newOperatorOptions;
                         };

                         return (
                              <>
                                   <CForm onSubmit={handleSubmit} noValidate>
                                        <div className="d-flex align-items-center justify-content-end">
                                             <CButton onClick={handleResetAll}>
                                                  <CLabel>clear all</CLabel>
                                                  <CIcon icon="iconDeleteCircle" />
                                             </CButton>
                                             <CButton color="primary" onClick={handleSubmit} type="submit" className="filter-button">
                                                  <CLabel>Apply</CLabel>
                                             </CButton>
                                        </div>
                                        {values.map((item, index) => {
                                             const typeValid = get(errors, `[${index}].type`);
                                             return (
                                                  <Fragment key={index}>
                                                       <div className="d-flex align-items-top justify-content-end mt-2">
                                                            <div className="select-filter">
                                                                 <Select
                                                                      classNamePrefix="react-select"
                                                                      name="type"
                                                                      className={`${typeValid ? 'is-invalid' : ''}`}
                                                                      placeholder="Select..."
                                                                      options={filterOptions.filter((option) =>
                                                                           values.every((value) => value.type !== option.value),
                                                                      )}
                                                                      value={
                                                                           filterOptions.find((option) => option.value === item.type) || {
                                                                                value: '',
                                                                                label: 'Select...',
                                                                           }
                                                                      }
                                                                      onChange={(e) => {
                                                                           handleChangeFilter({
                                                                                valueFilter: e,
                                                                                position: index,
                                                                                type: 'type',
                                                                                setValues: setValues,
                                                                                item: item,
                                                                           });
                                                                           setShowTooltip(true);
                                                                      }}
                                                                      onMenuOpen={() => setShowTooltip(true)}
                                                                      onMenuClose={() => setShowTooltip(false)}
                                                                 />
                                                                 {!!typeValid ? (
                                                                      <CInvalidFeedback className="d-block mb-0">{typeValid}</CInvalidFeedback>
                                                                 ) : (
                                                                      ''
                                                                 )}
                                                            </div>
                                                            <div className="select-filter-operator">
                                                                 <Select
                                                                      classNamePrefix="react-select"
                                                                      name="operator"
                                                                      placeholder="Select..."
                                                                      options={getOperatorOptions(item)}
                                                                      value={
                                                                           getOperatorOptions(item).find(
                                                                                (option) => option.value === item.operator,
                                                                           ) || {
                                                                                value: '',
                                                                                label: 'Select...',
                                                                           }
                                                                      }
                                                                      onChange={(e) => {
                                                                           handleChangeFilter({
                                                                                valueFilter: e,
                                                                                position: index,
                                                                                type: 'operator',
                                                                                setValues: setValues,
                                                                                item: item,
                                                                           });
                                                                           setShowTooltip(true);
                                                                      }}
                                                                      onMenuOpen={() => setShowTooltip(true)}
                                                                      onMenuClose={() => setShowTooltip(false)}
                                                                 />
                                                            </div>
                                                            <div className="field-value-search">
                                                                 {item.type === 'categoryId' ? (
                                                                      <>
                                                                           <Select
                                                                                classNamePrefix="react-select"
                                                                                name="value"
                                                                                className={`${get(errors, `[${index}].value`) ? 'is-invalid' : ''}`}
                                                                                options={categoryOptions}
                                                                                value={categoryOptions.find((category) => category.value === item.value)}
                                                                                onChange={(e) => {
                                                                                     handleChangeCategory(e, index, setValues);
                                                                                     setShowTooltip(true);
                                                                                }}
                                                                                onMenuOpen={() => setShowTooltip(true)}
                                                                                onMenuClose={() => setShowTooltip(false)}
                                                                                isDisabled={item.operator === 'nex'}
                                                                           />
                                                                           {get(errors, `[${index}].value`) && (
                                                                                <CInvalidFeedback className="d-block mb-0">
                                                                                     {get(errors, `[${index}].value`)}
                                                                                </CInvalidFeedback>
                                                                           )}
                                                                      </>
                                                                 ) : item.operator === 'checklist' || item.operator === 'notChecklist' ? (
                                                                      <>
                                                                           <Select
                                                                                classNamePrefix="react-select"
                                                                                name="value"
                                                                                isMulti
                                                                                className={`${get(errors, `[${index}].value`) ? 'is-invalid' : ''}`}
                                                                                options={item.type === 'listenerName' ? listenerNameOptions : []}
                                                                                value={
                                                                                     item.type === 'listenerName' && Array.isArray(item.value)
                                                                                          ? listenerNameOptions.filter((option) => item.value.includes(option.value))
                                                                                          : []
                                                                                }
                                                                                onChange={(selectedOptions) => {
                                                                                     handleChangeChecklist(selectedOptions, index, setValues);
                                                                                     setShowTooltip(true);
                                                                                }}
                                                                                onMenuOpen={() => setShowTooltip(true)}
                                                                                onMenuClose={() => setShowTooltip(false)}
                                                                                placeholder={item.type === 'listenerName' ? 'Select listeners...' : 'No options available'}
                                                                           />
                                                                           {get(errors, `[${index}].value`) && (
                                                                                <CInvalidFeedback className="d-block mb-0">
                                                                                     {get(errors, `[${index}].value`)}
                                                                                </CInvalidFeedback>
                                                                           )}
                                                                      </>
                                                                 ) : (
                                                                      <>
                                                                           <CInput
                                                                                value={item.value}
                                                                                name="value"
                                                                                className={`${get(errors, `[${index}].value`) ? 'is-invalid' : ''}`}
                                                                                onChange={(e) => handleChangeValueFilter(e, index, setValues)}
                                                                                disabled={item.operator === 'nex'}
                                                                           />
                                                                           {get(errors, `[${index}].value`) && (
                                                                                <CInvalidFeedback className="d-block mb-0">
                                                                                     {get(errors, `[${index}].value`)}
                                                                                </CInvalidFeedback>
                                                                           )}
                                                                      </>
                                                                 )}
                                                            </div>
                                                            {values.length > 1 && (
                                                                 <CButton
                                                                      className="button-group field-parameter-button"
                                                                      onClick={() => {
                                                                           handleDeleteFilter(index, setValues);
                                                                      }}
                                                                      style={typeValid ? { marginBottom: '20px' } : {}}
                                                                 >
                                                                      <CIcon icon="iconDeleteField" className="icon-delete" />
                                                                 </CButton>
                                                            )}
                                                       </div>
                                                  </Fragment>
                                             );
                                        })}

                                        {values.length < 5 && (
                                             <CFormGroup className=" d-flex align-items-center justify-content-end mt-1 mb-0">
                                                  <CButton
                                                       className="btn-add-row"
                                                       onClick={() =>
                                                            setTimeout(() => {
                                                                 setValues([...values, { type: '', operator: 'ct', value: '' }]);
                                                                 dispatch(
                                                                      setFilterEditFormCategories([
                                                                           ...values,
                                                                           { type: '', operator: 'ct', value: '' },
                                                                      ]),
                                                                 );
                                                            }, 100)
                                                       }
                                                  >
                                                       <CIcon icon="iconAddField" className="icon-add" />
                                                       <CLabel className="add-row">ADD ROW</CLabel>
                                                  </CButton>
                                             </CFormGroup>
                                        )}
                                   </CForm>
                              </>
                         );
                    }}
               </Formik>
          </div>
     );
};


FormFilter.propTypes = {
     setShowTooltip: PropTypes.func,
};

export default FormFilter;
